import { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import isEmpty from 'lodash/isEmpty'
import 'styled-components/macro'
import {
  useFlexLayout,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table'
import type { Row } from 'react-table'
import { useIntl } from 'react-intl'
import _isEqual from 'lodash/isEqual'

import type { TableProperties, TableWithPaginationType } from './types'
import HeaderSorting from './HeaderSorting'
import Pagination from 'components/Table/Pagination'
import { isCharsThresholdMet } from 'components/Table/utils'
import { commonMessages } from 'app/i18n/messages'
import { ROWS_PER_PAGE_OPTIONS } from './consts'
import {
  StyledTableContainer,
  StyledBodyTd,
  StyledBodyTr,
  StyledBodyTrMobile,
  StyledHeaderTh,
  StyledHeaderTr,
  StyledNoRows,
  StyledTableBody,
  StyledTableBodyMobile,
  StyledTableDesktop,
  StyledTableHeader,
  StyledTableMobile,
  TableHiddenAbsoluteBlock,
} from './styles'
import { useResizeObserver } from 'app/hooks/useResizeObserver'
import { SpinnerCentered } from 'components/Loader'

export function Table<T extends Record<any, any> & { rowAsLinkUrl?: string }>({
  columns,
  data,
  searchValue,
  onRowClick,
  onRowSelect,
  defaultSortBy,
  globalFilter,
  sortBy,
  setSortBy,
  rowsCountCallback,
  hiddenColumns = [],
  stripedRows = false,
  rowStyles,
  tableStyles,
  isPaginated = true,
  $shouldRowBeLink = false, // if true row.original.rowAsLinkUrl must be provided
  mobileViewComponent: MobileView,
  NoResultsComponent,
  fieldAsUniqueKey,
  disableSortBy = false,
  fetchData,
  paginatedPageCount = 0,
  itemCount,
  loading,
}: PropsWithChildren<TableProperties<T>>) {
  const { formatMessage } = useIntl()

  const pc = !!fetchData ? { pageCount: paginatedPageCount } : {}

  const options = {
    columns,
    data,
    disableSortRemove: true,
    initialState: {
      pageIndex: 0,
      pageSize: 10,
      sortBy: defaultSortBy
        ? [{ id: defaultSortBy.id, desc: defaultSortBy.desc }]
        : [],
      hiddenColumns,
    },
    disableMultiSort: true,
    manualSortBy: !!fetchData,
    autoResetSortBy: false,
    autoResetPage: false,
    manualPagination: !!fetchData,
    disableSortBy,
    ...pc,
  }
  const {
    getTableProps,
    getTableBodyProps,
    toggleAllRowsSelected,
    headerGroups,
    rows,
    prepareRow,
    selectedFlatRows,
    setGlobalFilter,
    setSortBy: setTableSortBy,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, sortBy: internalSortBy },
  } = useTable(
    { ...options, globalFilter },
    useFlexLayout,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect
  ) as TableWithPaginationType<T>
  const [selectedRowIndex, setSelectedRowId] = useState<number>()

  const showPagination =
    isPaginated &&
    (rows.length > ROWS_PER_PAGE_OPTIONS[0] || paginatedPageCount !== 0)
  const tableData = showPagination && !paginatedPageCount ? page : rows
  const isTableEmpty = isEmpty(tableData)
  const tableSortBy = internalSortBy[0]
  const oldTableSortBy = useRef(tableSortBy)

  useEffect(() => {
    if (onRowSelect && selectedFlatRows?.length > 0) {
      onRowSelect(selectedFlatRows[0])
    }
  }, [onRowSelect, selectedFlatRows])

  useEffect(() => {
    setGlobalFilter(isCharsThresholdMet(searchValue) ? searchValue : null)
  }, [setGlobalFilter, searchValue, data])

  useEffect(() => {
    if (fetchData) {
      fetchData({ pageIndex, pageSize, sort: tableSortBy })
    }
  }, [fetchData, pageIndex, pageSize, tableSortBy])

  // when external sortBy state changes we are applying changes to internal tableSortBy state
  useEffect(() => {
    const oldSortBy = oldTableSortBy.current
    if (sortBy && !_isEqual(sortBy, oldSortBy)) {
      oldTableSortBy.current = sortBy
      setTableSortBy([sortBy])
    }
  }, [sortBy, setTableSortBy])

  // when internal tableSortBy state changes we are applying changes to external sortBy state
  useEffect(() => {
    const oldSortBy = oldTableSortBy.current
    if (setSortBy && !_isEqual(tableSortBy, oldSortBy)) {
      oldTableSortBy.current = tableSortBy
      setSortBy(tableSortBy)
    }
  }, [tableSortBy, setSortBy])

  useEffect(() => {
    if (rowsCountCallback) {
      rowsCountCallback(rows.length)
    }
  }, [rows, rowsCountCallback])

  const rowProps = useMemo(
    () => (row: Row<T>) => {
      const onClick = onRowClick
        ? () => onRowClick(row)
        : onRowSelect
        ? (event: { target: { cellIndex: string } }) => {
            if (!event.target?.cellIndex) {
              setSelectedRowId(row.index)
              toggleAllRowsSelected(false)
              row.toggleRowSelected()
            }
          }
        : undefined

      return {
        onClick,
        style: {},
      }
    },
    [onRowClick, onRowSelect, toggleAllRowsSelected]
  )

  const tableContainerWidthRef = useRef<HTMLDivElement | null>(null)
  const [tableContainerWidth] = useResizeObserver(tableContainerWidthRef)

  const rowAsLinkAttributes = (row: Row<T>) =>
    $shouldRowBeLink ? { to: row.original?.rowAsLinkUrl } : {}

  return (
    <StyledTableContainer>
      <TableHiddenAbsoluteBlock ref={tableContainerWidthRef} />
      <StyledTableDesktop
        {...getTableProps()}
        $showPagination={showPagination}
        css={tableStyles}
      >
        {NoResultsComponent && isTableEmpty ? null : (
          <StyledTableHeader>
            {headerGroups.map((headerGroup, idx) => (
              <StyledHeaderTr {...headerGroup.getHeaderGroupProps()} key={idx}>
                {headerGroup.headers.map((column, idx) => (
                  <StyledHeaderTh
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    key={idx}
                    percentWidth={column.percentWidth}
                    css={column.cellStyles}
                    title={
                      column.canSort ? formatMessage(commonMessages.sort) : ''
                    }
                  >
                    {column.render('Header')}
                    <HeaderSorting
                      isSorted={column.isSorted && column.canSort}
                      isSortedDesc={column.isSortedDesc}
                    />
                  </StyledHeaderTh>
                ))}
              </StyledHeaderTr>
            ))}
          </StyledTableHeader>
        )}

        {loading && (
          <StyledNoRows>
            <SpinnerCentered />
          </StyledNoRows>
        )}

        {NoResultsComponent && isTableEmpty && !loading && NoResultsComponent}
        {!NoResultsComponent && isTableEmpty && (
          <StyledNoRows>
            {formatMessage(commonMessages.noResultsFound)}
          </StyledNoRows>
        )}
        {!isTableEmpty && !loading && (
          <StyledTableBody {...getTableBodyProps()}>
            {tableData.map((row, idx) => {
              prepareRow(row)
              return (
                <StyledBodyTr
                  as={$shouldRowBeLink ? Link : 'div'}
                  {...rowAsLinkAttributes(row)}
                  {...row.getRowProps(rowProps(row))}
                  key={
                    fieldAsUniqueKey ? row.original?.[fieldAsUniqueKey] : idx
                  }
                  $isStriped={stripedRows}
                  $showPagination={showPagination}
                  $shouldRowBeLink={$shouldRowBeLink}
                  $rowIndex={row.index}
                  $selectedRowIndex={selectedRowIndex}
                  css={rowStyles}
                >
                  {row.cells.map((cell, idx) => {
                    return (
                      <StyledBodyTd
                        {...cell.getCellProps()}
                        key={idx}
                        percentWidth={cell.column.percentWidth}
                        css={cell.column.cellStyles}
                      >
                        {cell.render('Cell')}
                      </StyledBodyTd>
                    )
                  })}
                </StyledBodyTr>
              )
            })}
          </StyledTableBody>
        )}
      </StyledTableDesktop>
      <StyledTableMobile {...getTableProps()}>
        {isTableEmpty && !NoResultsComponent && (
          <StyledNoRows>
            {formatMessage(commonMessages.noResultsFound)}
          </StyledNoRows>
        )}
        {isTableEmpty && !!NoResultsComponent && NoResultsComponent}
        {!MobileView && (
          <StyledNoRows>
            {formatMessage(commonMessages.noMobileSupport)}
          </StyledNoRows>
        )}
        {!isTableEmpty && MobileView && (
          <StyledTableBodyMobile {...getTableBodyProps()}>
            {tableData.map((row, idx) => {
              prepareRow(row)
              return (
                <StyledBodyTrMobile
                  {...row.getRowProps(rowProps(row))}
                  key={
                    fieldAsUniqueKey ? row.original?.[fieldAsUniqueKey] : idx
                  }
                  $rowIndex={row.index}
                  $selectedRowIndex={selectedRowIndex}
                  css={rowStyles}
                >
                  <MobileView values={row.values} original={row.original} />
                </StyledBodyTrMobile>
              )
            })}
          </StyledTableBodyMobile>
        )}
      </StyledTableMobile>
      {showPagination && (
        <Pagination<T>
          gotoPage={gotoPage}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          previousPage={previousPage}
          nextPage={nextPage}
          pageOptions={pageOptions}
          pageCount={paginatedPageCount !== 0 ? paginatedPageCount : pageCount}
          itemCount={itemCount}
          setPageSize={setPageSize}
          pageIndex={pageIndex}
          pageSize={pageSize}
          rowsLength={
            paginatedPageCount !== 0 ? paginatedPageCount : rows.length
          }
          width={tableContainerWidth}
        />
      )}
    </StyledTableContainer>
  )
}
